import { MAX_LEVEL } from './config';
import { createGameProgress } from './state';
import type { Difficulty, GameProgress, GameState, MusicTrackId, WeaponKind } from './types';

export const PERSISTENCE_NAMESPACE = 'armada-tanks-defense-grid';
export const PERSISTENCE_KEY = `${PERSISTENCE_NAMESPACE}:progress`;
export const PERSISTENCE_VERSION = 1 as const;
export const PERSISTENCE_SCHEMA = `${PERSISTENCE_NAMESPACE}/progress` as const;

export interface ProgressSnapshot {
  schema: typeof PERSISTENCE_SCHEMA;
  version: typeof PERSISTENCE_VERSION;
  data: GameProgress;
}

/** Minimal storage contract keeps this module usable in tests and non-DOM clients. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const WEAPON_KINDS: readonly WeaponKind[] = ['electro', 'flame', 'rocket'];
const DIFFICULTIES: readonly Difficulty[] = ['easy', 'normal', 'hard', 'extreme'];
const MUSIC_TRACKS: readonly MusicTrackId[] = ['city', 'electronic', 'synth'];
const MAX_UPGRADE_LEVEL = 3;
const MAX_RESOURCE_VALUE = 1_000_000_000;

type RecordValue = Record<string, unknown>;

/**
 * Creates a JSON-safe, version-independent snapshot from progress data.
 * Only the explicit GameProgress fields are copied; runtime/Three.js state is ignored.
 */
export function snapshot(progress: GameProgress): ProgressSnapshot {
  return {
    schema: PERSISTENCE_SCHEMA,
    version: PERSISTENCE_VERSION,
    data: normalizeProgress(progress),
  };
}

/** Alias with a descriptive name for callers exchanging snapshots with a server. */
export const createProgressSnapshot = snapshot;

/**
 * Validates and migrates an unknown snapshot into the current pure data model.
 * Missing fields in legacy data receive safe defaults; unknown/future schemas are rejected.
 */
export function rehydrate(value: unknown): GameProgress | null {
  const migrated = migrateSnapshot(value);
  return migrated ? migrated.data : null;
}

/** Alias with a descriptive name for multiplayer or replay code. */
export const rehydrateProgress = rehydrate;

/** Migrates current, v0, and unversioned legacy save shapes to the current envelope. */
export function migrateSnapshot(value: unknown): ProgressSnapshot | null {
  const extracted = extractData(value);
  if (!extracted) return null;

  return {
    schema: PERSISTENCE_SCHEMA,
    version: PERSISTENCE_VERSION,
    data: normalizeProgress(extracted.data),
  };
}

/** Loads and rehydrates the namespaced save. Storage or JSON failures are treated as no save. */
export function load(storage?: StorageLike): GameProgress | null {
  const target = resolveStorage(storage);
  if (!target) return null;

  let raw: string | null;
  try {
    raw = target.getItem(PERSISTENCE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return null;
  }

  const migrated = migrateSnapshot(parsed);
  if (!migrated) return null;

  if (!isCurrentSnapshot(parsed)) {
    try {
      target.setItem(PERSISTENCE_KEY, JSON.stringify(migrated));
    } catch {
      // A read remains useful even when a private-mode storage cannot be written.
    }
  }

  return migrated.data;
}

/** Saves only the serializable progress snapshot under the game namespace. */
export function save(progress: GameProgress, storage?: StorageLike): boolean {
  const target = resolveStorage(storage);
  if (!target) return false;

  try {
    target.setItem(PERSISTENCE_KEY, JSON.stringify(snapshot(progress)));
    return true;
  } catch {
    return false;
  }
}

/** Removes the namespaced save without touching other localStorage entries. */
export function clear(storage?: StorageLike): boolean {
  const target = resolveStorage(storage);
  if (!target) return false;

  try {
    target.removeItem(PERSISTENCE_KEY);
    return true;
  } catch {
    return false;
  }
}

export const loadProgress = load;
export const saveProgress = save;
export const clearProgress = clear;

/** Extracts durable data from a runtime state while deliberately dropping Three.js objects. */
export function snapshotFromState(state: GameState): ProgressSnapshot {
  return snapshot({
    level: state.level,
    score: state.score,
    credits: state.credits,
    weapon: state.weapon,
    unlocked: state.unlocked,
    upgrades: state.upgrades,
    cameraMode: 'top',
    cameraSettings: state.cameraSettings,
    difficulty: state.difficulty,
    musicTrack: state.musicTrack,
    musicVolume: state.musicVolume,
    soundVolume: state.soundVolume,
    sound: state.sound,
    completed: state.completed || state.victory,
    gameOver: state.gameOver,
  });
}

function extractData(value: unknown): { data: RecordValue } | null {
  if (!isRecord(value)) return null;

  if (value.schema !== undefined && value.schema !== PERSISTENCE_SCHEMA) return null;
  if (value.version !== undefined) {
    if (value.version === PERSISTENCE_VERSION) {
      if (value.schema !== PERSISTENCE_SCHEMA) return null;
      return isRecord(value.data) ? { data: value.data } : null;
    }
    if (value.version === 0) {
      const legacyData = isRecord(value.data) ? value.data : value;
      return { data: legacyData };
    }
    return null;
  }

  // v0 was an unversioned object written directly as progress data.
  return { data: value };
}

function normalizeProgress(value: unknown): GameProgress {
  const defaults = createGameProgress();
  const source = isRecord(value) ? value : {};
  const unlockedSource = isRecord(source.unlocked) ? source.unlocked : {};
  const upgradesSource = isRecord(source.upgrades) ? source.upgrades : {};
  const cameraSource = isRecord(source.camera) ? source.camera : {};
  const settingsSource = isRecord(cameraSource.settings)
    ? cameraSource.settings
    : isRecord(source.cameraSettings) ? source.cameraSettings : {};

  const unlocked = {
    electro: readBoolean(unlockedSource.electro, defaults.unlocked.electro),
    flame: readBoolean(unlockedSource.flame, defaults.unlocked.flame),
    rocket: readBoolean(unlockedSource.rocket, defaults.unlocked.rocket),
  };
  // The starter weapon is an invariant, even if a tampered save says otherwise.
  unlocked.electro = true;

  const selectedWeapon = readEnum(source.weapon, WEAPON_KINDS, defaults.weapon);
  const weapon = unlocked[selectedWeapon] ? selectedWeapon : firstUnlocked(unlocked);
  const legacyCompleted = source.victory;
  return {
    level: readInteger(source.level, defaults.level, 1, MAX_LEVEL),
    score: readInteger(source.score, defaults.score, 0, MAX_RESOURCE_VALUE),
    credits: readInteger(source.credits, defaults.credits, 0, MAX_RESOURCE_VALUE),
    weapon,
    unlocked,
    upgrades: {
      power: readInteger(upgradesSource.power, defaults.upgrades.power, 0, MAX_UPGRADE_LEVEL),
      cooling: readInteger(upgradesSource.cooling, defaults.upgrades.cooling, 0, MAX_UPGRADE_LEVEL),
      shield: readInteger(upgradesSource.shield, defaults.upgrades.shield, 0, MAX_UPGRADE_LEVEL),
    },
    // Camera mode is retained in the save shape for compatibility, but the
    // game now has one camera only.
    cameraMode: 'top',
    cameraSettings: {
      tilt: readNumber(settingsSource.tilt, defaults.cameraSettings.tilt, 35, 65),
      distance: readNumber(settingsSource.distance, defaults.cameraSettings.distance, 18, 40),
      fov: readNumber(settingsSource.fov, defaults.cameraSettings.fov, 38, 62),
      lookAhead: readNumber(settingsSource.lookAhead, defaults.cameraSettings.lookAhead, -8, 8),
    },
    difficulty: readEnum(source.difficulty, DIFFICULTIES, defaults.difficulty),
    musicTrack: readEnum(source.musicTrack, MUSIC_TRACKS, defaults.musicTrack),
    musicVolume: readVolume(source.musicVolume, defaults.musicVolume),
    soundVolume: readVolume(source.soundVolume, defaults.soundVolume),
    sound: readBoolean(source.sound ?? source.soundEnabled, defaults.sound),
    completed: readBoolean(source.completed ?? legacyCompleted, defaults.completed),
    gameOver: readBoolean(source.gameOver, defaults.gameOver),
  };
}

function firstUnlocked(unlocked: Record<WeaponKind, boolean>): WeaponKind {
  return WEAPON_KINDS.find((kind) => unlocked[kind]) ?? 'electro';
}

function resolveStorage(storage?: StorageLike): StorageLike | null {
  if (storage) return storage;

  try {
    const candidate = (globalThis as { localStorage?: unknown }).localStorage;
    return isStorageLike(candidate) ? candidate : null;
  } catch {
    return null;
  }
}

function isStorageLike(value: unknown): value is StorageLike {
  if (!isRecord(value)) return false;
  return typeof value.getItem === 'function'
    && typeof value.setItem === 'function'
    && typeof value.removeItem === 'function';
}

function isCurrentSnapshot(value: unknown): value is ProgressSnapshot {
  return isRecord(value)
    && value.schema === PERSISTENCE_SCHEMA
    && value.version === PERSISTENCE_VERSION
    && isRecord(value.data);
}

function isRecord(value: unknown): value is RecordValue {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function readEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && allowed.includes(value as T) ? value as T : fallback;
}

function readNumber(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

function readVolume(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  const normalized = value > 1 ? value / 100 : value;
  return Math.min(1, Math.max(0, normalized));
}

function readInteger(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}
